import { useEffect, useMemo, useState } from "react";
import api, { formatDkk } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, RotateCcw } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import RequestStatusTabs from "@/components/customer/RequestStatusTabs";
import CustomerRequestCard from "@/components/customer/CustomerRequestCard";
import { PageSkeleton } from "@/components/LoadingSkeleton";

export default function CustomerDashboard() {
  const [problems, setProblems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestTab, setRequestTab] = useState("all");
  const [params] = useSearchParams();
  const submittedId = params.get("submitted");

  const reload = async ({ initial = false } = {}) => {
    try {
      const problemResponse = await api.get("/problems/mine");
      setProblems(problemResponse.data || []);
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    reload({ initial: true });
    api.get("/categories").then((response) => setCategories(response.data || [])).catch(() => {});
    const timer = setInterval(reload, 6000);
    return () => clearInterval(timer);
  }, []);

  const categoryNames = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category.name])),
    [categories],
  );

  const requestGroups = useMemo(() => ({
    all: problems,
    action: problems.filter((problem) =>
      problem.clarification_unanswered
      || ((problem.proposal_count || 0) > 0 && !["offer_accepted", "completed", "cancelled"].includes(problem.status))
    ),
    active: problems.filter((problem) => problem.status === "offer_accepted"),
    closed: problems.filter((problem) => ["completed", "cancelled", "rejected"].includes(problem.status)),
  }), [problems]);

  const requestTabs = [
    { id: "all", label: "Alle", count: requestGroups.all.length },
    { id: "action", label: "Kræver handling", count: requestGroups.action.length },
    { id: "active", label: "Aktive", count: requestGroups.active.length },
    { id: "closed", label: "Afsluttede", count: requestGroups.closed.length },
  ];

  const upcoming = problems.filter((problem) =>
    problem.status === "offer_accepted"
    && problem.scheduled_at
    && problem.consultation_schedule_state === "confirmed"
  );
  const done = problems.filter((problem) => problem.status === "completed");
  const previousProviders = useMemo(() => {
    const unique = new Map();
    for (const problem of problems) {
      if (problem.status !== "completed" || !problem.accepted_expert_id) continue;
      if (!unique.has(problem.accepted_expert_id)) {
        unique.set(problem.accepted_expert_id, {
          id: problem.accepted_expert_id,
          name: problem.accepted_expert_name || "Din tidligere hjælper",
          problemTitle: problem.title,
          category: categoryNames[problem.category_id],
        });
      }
    }
    return [...unique.values()];
  }, [problems, categoryNames]);
  const actionCount = requestGroups.action.length;

  if (loading) return <PageSkeleton cards={4}/>;

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-7 sm:px-6 sm:py-9">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-[28px] font-semibold tracking-[-0.02em] text-brand-ink">Mine forespørgsler</h1>
          <p className="mt-1 text-sm text-brand-body">Her kan du følge status på dine opgaver og svar fra eksperter.</p>
        </div>
        <Link to="/beskriv-problem" className="inline-flex min-h-11 items-center rounded-xl bg-brand-fresh px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-green" data-testid="new-problem-cta">
          Opret forespørgsel
        </Link>
      </div>

      {submittedId && (
        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-brand-line-strong bg-[#EFF7F1] p-3 text-sm" data-testid="submitted-banner">
          <CheckCircle2 className="mt-0.5 shrink-0 text-brand-fresh" size={16}/>
          <div>
            <div className="font-medium text-brand-ink">Din forespørgsel er sendt</div>
            <div className="text-brand-body">Du får besked når en ekspert sender et spørgsmål eller prisforslag.</div>
          </div>
        </div>
      )}

      <section className="mt-5" data-testid="customer-request-overview">
        <RequestStatusTabs items={requestTabs} active={requestTab} onChange={setRequestTab}/>

        <div className="mt-3 space-y-3">
          {requestGroups[requestTab].map((problem) => (
            <CustomerRequestCard
              key={problem.id}
              problem={problem}
              category={categoryNames[problem.category_id]}
            />
          ))}
          {requestGroups[requestTab].length === 0 && (
            <div className="rounded-2xl border border-dashed border-brand-line bg-white px-5 py-8 text-center text-sm text-brand-muted">
              Ingen forespørgsler i denne visning.
            </div>
          )}
        </div>
      </section>

      {actionCount > 0 && (
        <Link to="/dashboard" onClick={() => setRequestTab("action")} className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-brand-line-strong bg-brand-soft p-4" data-testid="customer-action-banner">
          <div>
            <div className="font-semibold text-brand-green">Du har nye svar på {actionCount} {actionCount === 1 ? "forespørgsel" : "forespørgsler"}</div>
            <div className="mt-1 text-sm text-[#4D6E64]">Åbn dem for at læse beskeder eller vælge et prisforslag.</div>
          </div>
          <ChevronRight className="shrink-0 text-brand-green"/>
        </Link>
      )}

      {previousProviders.length > 0 && <section className="mt-9" data-testid="previous-providers">
        <h2 className="font-display text-lg font-semibold text-brand-ink">Tidligere hjælp</h2>
        <p className="mt-1 text-sm text-brand-body">Start en ny forespørgsel hos en person du allerede har fået hjælp af.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {previousProviders.map((provider) => {
            const firstName = provider.name.split(" ")[0];
            return <article key={provider.id} className="rounded-2xl border border-brand-line-strong bg-white p-4" data-testid={`previous-provider-${provider.id}`}>
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-brand-fresh">{provider.name.charAt(0)}</span>
                <div className="min-w-0"><Link to={`/eksperter/${provider.id}`} className="font-semibold text-brand-ink hover:text-brand-fresh">{provider.name}</Link><div className="mt-1 text-xs text-brand-muted">Hjalp med: {provider.problemTitle}{provider.category ? ` · ${provider.category}` : ""}</div></div>
              </div>
              <Link
                to={`/beskriv-problem?ekspert=${provider.id}&gentag=1`}
                onClick={() => trackEvent("repeat_provider_clicked", { expert_id: provider.id, metadata: { route: "/dashboard" } })}
                className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-brand-fresh px-4 text-sm font-semibold text-white hover:bg-brand-green"
                data-testid={`repeat-provider-${provider.id}`}
              ><RotateCcw size={15}/>Få hjælp af {firstName} igen</Link>
            </article>;
          })}
        </div>
      </section>}

      <section className="mt-9">
        <h2 className="font-display text-lg font-semibold text-brand-ink">Kommende samtaler</h2>
        <div className="mt-3 space-y-2">
          {upcoming.map((problem) => (
            <Link key={problem.id} to={`/forespoergsler/${problem.id}`} className="card-outline flex items-center justify-between p-3.5 hover-lift" data-testid={`upcoming-${problem.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-brand-ink">{problem.title}</div>
                <div className="text-[11px] text-brand-muted">
                  Med {problem.accepted_expert_name || "eksperten"} · {new Date(problem.scheduled_at).toLocaleString("da-DK")}
                </div>
              </div>
              <ChevronRight size={16} className="shrink-0 text-brand-muted"/>
            </Link>
          ))}
          {upcoming.length === 0 && <div className="text-sm text-brand-muted">Ingen kommende samtaler.</div>}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg font-semibold text-brand-ink">Tidligere konsultationer</h2>
        <div className="mt-3 space-y-2">
          {done.map((problem) => {
            const terms = problem.accepted_terms || {};
            const amount = terms.fixed_price || terms.estimated_price_max || terms.estimated_price_min;
            return <Link key={problem.id} to={`/forespoergsler/${problem.id}`} className="card-outline flex items-center justify-between gap-3 p-3.5 hover-lift" data-testid={`history-${problem.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-brand-ink">{problem.title}</div>
                <div className="mt-1 text-[11px] text-brand-muted">
                  {problem.completed_at ? new Date(problem.completed_at).toLocaleDateString("da-DK") : "Afsluttet"} · {problem.accepted_expert_name || "Ekspert"}
                </div>
              </div>
              <div className="shrink-0 text-right">
                {amount && <div className="font-mono text-xs text-brand-ink">{formatDkk(amount)}</div>}
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-brand-green">Se forløb <ChevronRight size={11}/></div>
              </div>
            </Link>;
          })}
          {done.length === 0 && <div className="text-sm text-brand-muted">Ingen afsluttede konsultationer endnu.</div>}
        </div>
      </section>
    </div>
  );
}

